import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';

// Components
import { AddBudgetItemForm } from '../../../../components/forms/AddBudgetItemForm';
import { BudgetItemsManagementTable } from '../../../../components/display/tables/formTables/managementTables/BudgetItemsManagementTable';

// Types
import { BudgetItemManagementLoader } from './types';
import { BudgetItemManagement } from '../../../../components/forms/AddBudgetItemForm/types';

// Styles
import { Container, BudgetItemManagementSection } from './styles';

// Constants
import { formBudgetItemManagementNaming } from '../../../../components/forms/AddBudgetItemForm/constants';

// GQL
import { client } from '../../../../gql/client';
import {
  CREATE_BUDGET_ITEM,
  DELETE_BUDGET_ITEM,
  ACTIVATE_BUDGET_ITEM,
} from '../../../../gql/mutations/budget/budgetItems';
import { BUDGET_ITEM_WITH_CATEGORY } from '../../../../gql/queries/budget/budgetItems';

export default function BudgetItemsManagementPage(): JSX.Element {
  const loaderData = useAsyncValue() as BudgetItemManagementLoader;
  const revalidator = useRevalidator();

  const allItemCodes = [
    ...loaderData.activeItems,
    ...loaderData.inactiveItems,
  ].map((item) => item.code);

  const onReFetchData = useCallback(() => {
    revalidator.revalidate();
  }, []);

  const onFormSubmit = async (data: BudgetItemManagement) => {
    if (allItemCodes.some((code) => code === data.code)) {
      return 'El código de partida ya existe';
    }

    try {
      await client.mutate({
        mutation: CREATE_BUDGET_ITEM,
        variables: {
          input: {
            code: data.code,
            description: data.description,
            unit: data.unit,
            unitPrice: data.unitPrice,
            budgetCategoryId: data.budgetCategoryId,
          },
        },
        // ✅ CRÍTICO: Invalidar caché de queries relacionadas
        refetchQueries: [
          'GetBudgetItems', // Management loader
          'GetBudgetItemsByCategory', // ActionsValidation selector
          'GetBudgetCategories', // Categorías (por si cambia el count)
        ],
        awaitRefetchQueries: true,
      });

      // Revalidar datos después de crear (igual que users/vehicles)
      onReFetchData();
    } catch (error) {
      return 'Error al crear la partida presupuestaria';
    }
  };

  const onAddActiveBudgetItem = async (id: string) => {
    try {
      await client.mutate({
        mutation: DELETE_BUDGET_ITEM, // Desactiva (isActive: false)
        variables: { id },
        // ✅ CRÍTICO: Invalidar caché de queries relacionadas
        refetchQueries: [
          'GetBudgetItems', // Management loader
          'GetBudgetItemsByCategory', // ActionsValidation selector
          'GetBudgetCategories', // Categorías (por si cambia el count)
        ],
        awaitRefetchQueries: true,
      });
      onReFetchData();
    } catch (error) {
      // Error handled silently
    }
  };

  const onAddInactiveBudgetItem = async (id: string) => {
    try {
      await client.mutate({
        mutation: ACTIVATE_BUDGET_ITEM, // Activa (isActive: true)
        variables: { id },
        // ✅ CRÍTICO: Invalidar caché de queries relacionadas
        refetchQueries: [
          'GetBudgetItems', // Management loader
          'GetBudgetItemsByCategory', // ActionsValidation selector
          'GetBudgetCategories', // Categorías (por si cambia el count)
        ],
        awaitRefetchQueries: true,
      });
      onReFetchData();
    } catch (error) {
      // Error handled silently
    }
  };

  return (
    <Container>
      <BudgetItemManagementSection>
        <AddBudgetItemForm
          initialData={loaderData.cacheData}
          onFormSubmit={onFormSubmit}
          namingPersistForm={
            formBudgetItemManagementNaming.NEW_BUDGET_ITEM_MANAGEMENT_FORM
          }
          categories={loaderData.categories}
        />
      </BudgetItemManagementSection>

      <BudgetItemManagementSection>
        <BudgetItemsManagementTable
          onAddActiveBudgetItem={onAddActiveBudgetItem}
          onAddInactiveBudgetItem={onAddInactiveBudgetItem}
          data={{
            activeItems: loaderData.activeItems as any,
            inactiveItems: loaderData.inactiveItems as any,
          }}
        />
      </BudgetItemManagementSection>
    </Container>
  );
}
